import { Bot, User as UserIcon, Users } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InboxTypeEnum, IndexPath } from '@/Config/app/router/index.path';
import { User } from '@/Domain/api/User/dto/User.dto';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/Domain/components/Shared/Select/Select';
import { UserHelper } from '@/Domain/helper/User.helper';
import { useScenarioUsers } from '@/Domain/hooks/AI/useScenarioUsers.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

const AI_AGENT = 'ai_agent';
const BY_ME = 'by_me';
const ALL = 'all';

const getCurrentValue = (inboxType: string | null, filterUserId: string | null): string => {
  if (inboxType === InboxTypeEnum.OpenByAI) return AI_AGENT;
  if (inboxType === InboxTypeEnum.OpenByMe) return BY_ME;
  if (inboxType === InboxTypeEnum.OpenByOther && filterUserId) return filterUserId;
  return ALL;
};

const getUserLabel = (user: User) => UserHelper.getFullName(user) || user.email;

const getUserInitial = (firstName: string, email: string) =>
  firstName?.charAt(0) || email?.charAt(0)?.toUpperCase() || 'U';

const LEFT_VISUAL_CLASS = 'inline-flex size-5 shrink-0 items-center justify-center';
const FILTER_ICON_CLASS = 'size-4 text-muted-foreground';

const renderLeftIcon = (icon: ReactNode) => <span className={LEFT_VISUAL_CLASS}>{icon}</span>;

const renderUserAvatar = (avatarUrl: string | null, firstName: string, email: string) => (
  <Avatar className="size-5 shrink-0 rounded-md">
    <AvatarImage src={avatarUrl || undefined} />
    <AvatarFallback className="rounded-md text-xs">
      {getUserInitial(firstName, email)}
    </AvatarFallback>
  </Avatar>
);

export const TeammateFilter = () => {
  const { t } = useTranslation('inbox_v2');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { users } = useScenarioUsers();
  const { user: currentUser } = useUser();

  const inboxType = searchParams.get('type');
  const filterUserId = searchParams.get('filter_user_id');
  const currentValue = getCurrentValue(inboxType, filterUserId);

  const teammates = useMemo(() => {
    const seen = new Set<number>();
    return (users ?? [])
      .filter((u) => {
        if (u.id === currentUser?.id || seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      })
      .sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
  }, [users, currentUser?.id]);

  const handleSelect = (value: string) => {
    switch (value) {
      case ALL:
        navigate(IndexPath.inbox.openAllAssigned);
        break;
      case AI_AGENT:
        navigate(IndexPath.inbox.openByAI);
        break;
      case BY_ME:
        navigate(IndexPath.inbox.openByMe);
        break;
      default:
        navigate(`${IndexPath.inbox.openByOther}&filter_user_id=${value}`);
        break;
    }
  };

  const triggerLabel = useMemo(() => {
    switch (currentValue) {
      case ALL:
        return {
          icon: renderLeftIcon(<Users className={FILTER_ICON_CLASS} />),
          label: t('teammate_filter.all'),
        };
      case AI_AGENT:
        return {
          icon: renderLeftIcon(<Bot className={FILTER_ICON_CLASS} />),
          label: t('teammate_filter.ai_agent'),
        };
      case BY_ME:
        return {
          icon: currentUser
            ? renderUserAvatar(currentUser.avatar_url, currentUser.first_name, currentUser.email)
            : renderLeftIcon(<UserIcon className={FILTER_ICON_CLASS} />),
          label: currentUser ? getUserLabel(currentUser) : t('teammate_filter.you'),
        };
      default: {
        const teammate = teammates.find((u) => String(u.id) === currentValue);
        return {
          icon: teammate
            ? renderUserAvatar(teammate.avatar_url, teammate.first_name, teammate.email)
            : null,
          label: teammate ? getUserLabel(teammate) : currentValue,
        };
      }
    }
  }, [currentValue, teammates, currentUser, t]);

  return (
    <div className="mb-3 w-full">
      <p className="mb-1 text-xs text-foreground-subtle">{t('teammate_filter.label')}</p>
      <Select value={currentValue} onValueChange={handleSelect}>
        <SelectTrigger className="w-full" data-testid="teammate-filter-trigger">
          <div className="flex items-center gap-1.5 truncate">
            {triggerLabel.icon}
            <span className="truncate">{triggerLabel.label}</span>
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>
            <div className="flex items-center gap-1.5">
              {renderLeftIcon(<Users className={FILTER_ICON_CLASS} />)}
              {t('teammate_filter.all')}
            </div>
          </SelectItem>
          <SelectItem value={AI_AGENT}>
            <div className="flex items-center gap-1.5">
              {renderLeftIcon(<Bot className={FILTER_ICON_CLASS} />)}
              {t('teammate_filter.ai_agent')}
            </div>
          </SelectItem>
          {currentUser && (
            <SelectItem value={BY_ME}>
              <div className="flex items-center gap-1.5">
                {renderUserAvatar(
                  currentUser.avatar_url,
                  currentUser.first_name,
                  currentUser.email,
                )}
                {getUserLabel(currentUser)}
              </div>
            </SelectItem>
          )}
          {teammates.map((teammate) => (
            <SelectItem key={teammate.id} value={String(teammate.id)}>
              <div className="flex items-center gap-1.5">
                {renderUserAvatar(teammate.avatar_url, teammate.first_name, teammate.email)}
                {getUserLabel(teammate)}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
